import forestBackground from "../assets/forest/forest-wind-background.webp";
import rangerAtlas from "../assets/forest/ranger-atlas.webp";
import rangerLiftAtlas from "../assets/forest/ranger-lift-atlas.webp";
import birdSongAtlas from "../assets/forest/bird-song-atlas.webp";
import windAtlas from "../assets/forest/wind-atlas.webp";
import wildlifeAtlas from "../assets/forest/wildlife-atlas.webp";
import miningFxAtlas from "../assets/forest/mining-fx-atlas.webp";
import assetLayout from "../assets/forest/asset-layout.json";
import type { MiningPhase } from "../state/ui-types";
import type { ForestRect, ForestTarget } from "./types";

export const forestBackgroundUrl = forestBackground;
export const FOREST_WORLD_WIDTH = 1586;
export const FOREST_WORLD_HEIGHT = 992;

export interface ForestAtlas {
  src: string;
  width: number;
  height: number;
}

export interface ForestFrame {
  atlas: "ranger" | "rangerLift" | "birdSong" | "wind" | "wildlife" | "effects";
  rect: ForestRect;
  anchor: { x: number; y: number };
  position: { x: number; y: number };
  scale: number;
  scaleY?: number;
  transformMatrix?: readonly [number, number, number, number];
}

// Kept separate from animation logic so painted replacement frames need no engine changes.
export const FOREST_ATLASES: Record<ForestFrame["atlas"], ForestAtlas> = {
  ranger: { src: rangerAtlas, width: assetLayout.ranger.width, height: assetLayout.ranger.height },
  rangerLift: { src: rangerLiftAtlas, width: assetLayout.rangerLift.width, height: assetLayout.rangerLift.height },
  birdSong: { src: birdSongAtlas, width: assetLayout.birdSong.width, height: assetLayout.birdSong.height },
  wind: { src: windAtlas, width: assetLayout.windAtlas.width, height: assetLayout.windAtlas.height },
  wildlife: { src: wildlifeAtlas, width: 1536, height: 1024 },
  effects: { src: miningFxAtlas, width: assetLayout.miningFx.width, height: assetLayout.miningFx.height },
};

function frame(atlas: ForestFrame["atlas"], rect: ForestRect & { anchorX: number; anchorY: number }, position: { x: number; y: number }, scale: number): ForestFrame {
  return { atlas, rect, anchor: { x: rect.anchorX / rect.width, y: rect.anchorY / rect.height }, position, scale };
}

function flagFrame(name: "flagLeft" | "flagRight"): ForestFrame {
  const matrix = assetLayout.windAtlas.flags[name].neutralMatrix;
  return {
    atlas: "wind",
    rect: assetLayout.windAtlas.frames[name],
    anchor: { x: 0, y: 0 },
    position: { x: matrix.tx, y: matrix.ty },
    scale: matrix.a,
    scaleY: matrix.d,
    // Pixel scaling belongs to the crop box; this remaining matrix is shared
    // by the DOM fallback and Pixi, preserving the original sloped crossbar.
    transformMatrix: [1, matrix.b / matrix.a, matrix.c / matrix.d, 1],
  };
}

function grassFrame(name: keyof typeof assetLayout.windAtlas.grass): ForestFrame {
  const placement = assetLayout.windAtlas.grass[name];
  return frame("wind", assetLayout.windAtlas.frames[name], placement.position, placement.pixelScale);
}

const rangerRoot = { x: 875, y: 720 };
const rangerLiftScale = assetLayout.rangerLift.suggestedPixelScale;
const deerRoot = { x: 1500, y: 570 };
export const FOREST_CRYSTAL_ROOT = { x: 1080, y: 476 };
function handSupport(pose: { anchorX: number; anchorY: number; handContactX: number; handContactY: number }) {
  return {
    x: rangerRoot.x + (pose.handContactX - pose.anchorX) * rangerLiftScale,
    y: rangerRoot.y + (pose.handContactY - pose.anchorY) * rangerLiftScale,
  };
}
export const FOREST_CRYSTAL_SUPPORT = {
  lower: handSupport(assetLayout.rangerLift.frames.miningLiftLow),
  raised: handSupport(assetLayout.rangerLift.frames.miningLiftHigh),
} as const;

export function getForestCrystalSupport(lift: number) {
  const progress = Math.max(0, Math.min(1, lift));
  const { lower, raised } = FOREST_CRYSTAL_SUPPORT;
  return { x: lower.x + (raised.x - lower.x) * progress, y: lower.y + (raised.y - lower.y) * progress };
}
export const FOREST_BIRD_PERCH = assetLayout.birdSong.suggestedWorldAnchor;
export const FOREST_LANTERNS = [
  { x: 326, y: 414, coreWidth: 17, coreHeight: 32, haloWidth: 92, haloHeight: 112 },
  { x: 1218, y: 347, coreWidth: 34, coreHeight: 57, haloWidth: 160, haloHeight: 188 },
] as const;

export const FOREST_FRAMES = {
  flagLeft: flagFrame("flagLeft"),
  flagRight: flagFrame("flagRight"),
  grassOne: grassFrame("grassOne"),
  grassTwo: grassFrame("grassTwo"),
  grassThree: grassFrame("grassThree"),
  grassFour: grassFrame("grassFour"),
  grassFive: grassFrame("grassFive"),
  grassSix: grassFrame("grassSix"),
  rangerIdle: frame("ranger", assetLayout.ranger.frames.idle, rangerRoot, 0.8),
  rangerAttention: frame("ranger", assetLayout.ranger.frames.idleLook, rangerRoot, 0.8),
  rangerMining: frame("rangerLift", assetLayout.rangerLift.frames.miningLiftLow, rangerRoot, rangerLiftScale),
  rangerMiningSecond: frame("rangerLift", assetLayout.rangerLift.frames.miningLiftHigh, rangerRoot, rangerLiftScale),
  deerGrazing: frame("wildlife", assetLayout.wildlife.frames.deerGraze, deerRoot, 0.3),
  deerAlert: frame("wildlife", assetLayout.wildlife.frames.deerAlert, deerRoot, 0.3),
  birdPerched: frame("birdSong", assetLayout.birdSong.frames.idle, FOREST_BIRD_PERCH, assetLayout.birdSong.suggestedPixelScale),
  birdStretch: frame("birdSong", assetLayout.birdSong.frames.stretch, FOREST_BIRD_PERCH, assetLayout.birdSong.suggestedPixelScale),
  birdSinging: frame("birdSong", assetLayout.birdSong.frames.singing, FOREST_BIRD_PERCH, assetLayout.birdSong.suggestedPixelScale),
  crystalDormant: frame("effects", assetLayout.miningFx.frames.crystalDormant, FOREST_CRYSTAL_ROOT, 0.11),
  crystalActive: frame("effects", assetLayout.miningFx.frames.crystalActive, FOREST_CRYSTAL_ROOT, 0.11),
  runeEmission: frame("effects", { ...assetLayout.miningFx.frames.runes, anchorX: 0, anchorY: 0 }, { x: 1011, y: 501 }, 0.33),
  amberMote: frame("effects", assetLayout.miningFx.frames.mote, FOREST_CRYSTAL_ROOT, 0.05),
} satisfies Record<string, ForestFrame>;

export type ForestFrameName = keyof typeof FOREST_FRAMES;

export const FOREST_FLAGS = [
  { id: "flag-left", frame: "flagLeft", phase: 0.3, maxBend: 3.6, pinnedHeight: 45 },
  { id: "flag-right", frame: "flagRight", phase: 1.1, maxBend: 5.2, pinnedHeight: 45 },
] satisfies Array<{ id: string; frame: ForestFrameName; phase: number; maxBend: number; pinnedHeight: number }>;

export const FOREST_GRASS = ([
  { id: "grass-1", frame: "grassOne", phase: 0.4 },
  { id: "grass-2", frame: "grassTwo", phase: 1.3 },
  { id: "grass-3", frame: "grassThree", phase: 2.2 },
  { id: "grass-4", frame: "grassFour", phase: 3.1 },
  { id: "grass-5", frame: "grassFive", phase: 4.0 },
  { id: "grass-6", frame: "grassSix", phase: 4.9 },
] satisfies Array<{ id: string; frame: ForestFrameName; phase: number }>).sort((left, right) => FOREST_FRAMES[left.frame].position.y - FOREST_FRAMES[right.frame].position.y);

export const FOREST_TARGETS: Record<ForestTarget, ForestRect> = {
  ranger: { x: 677, y: 245, width: 355, height: 480 },
  altar: { x: 977, y: 410, width: 276, height: 284 },
  deer: { x: 1385, y: 415, width: 174, height: 160 },
  bird: { x: 271, y: 280, width: 86, height: 84 },
};

export function getForestFrameBounds(name: ForestFrameName): ForestRect {
  const { rect, position, anchor, scale } = FOREST_FRAMES[name];
  const width = rect.width * scale;
  const height = rect.height * (FOREST_FRAMES[name].scaleY ?? scale);
  return { x: position.x - anchor.x * width, y: position.y - anchor.y * height, width, height };
}

export interface StaticForestActor extends ForestRect {
  id: string;
  src: string;
  frame: ForestRect;
  atlas: Pick<ForestAtlas, "width" | "height">;
  transformMatrix?: readonly [number, number, number, number];
}

export function getStaticForestActors(phase: MiningPhase): StaticForestActor[] {
  const working = phase === "MINING" || phase === "STARTING";
  const ranger: ForestFrameName = phase === "MINING" ? "rangerMiningSecond" : phase === "STARTING" ? "rangerMining" : "rangerIdle";
  const descriptors: Array<{ id: string; frame: ForestFrameName }> = [
    ...FOREST_FLAGS,
    { id: "deer", frame: "deerGrazing" },
    { id: "bird", frame: "birdPerched" },
    { id: "ranger", frame: ranger },
    { id: "crystal", frame: working ? "crystalActive" : "crystalDormant" },
    ...FOREST_GRASS,
  ];
  return descriptors.map(({ id, frame: name }) => {
    const frame = FOREST_FRAMES[name];
    const atlas = FOREST_ATLASES[frame.atlas];
    const bounds = getForestFrameBounds(name);
    if (id === "crystal" && working) {
      const support = getForestCrystalSupport(phase === "MINING" ? 1 : 0);
      bounds.x += support.x - FOREST_CRYSTAL_ROOT.x;
      bounds.y += support.y - FOREST_CRYSTAL_ROOT.y;
    }
    return { id, ...bounds, src: atlas.src, frame: frame.rect, atlas: { width: atlas.width, height: atlas.height },
      ...(frame.transformMatrix ? { transformMatrix: frame.transformMatrix } : {}) };
  });
}
